import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

function errorText(error) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Could not save the utilization report';
}

// Signed-in scholars report how scholarship funds were used.
export default function ScholarshipUtilizationPage() {
  const { token } = useContext(AuthContext);
  const [applications, setApplications] = useState([]);
  const [selected, setSelected] = useState('');
  const [reports, setReports] = useState([]);
  const [paused, setPaused] = useState(false);
  const [missing, setMissing] = useState('');
  const [periodType, setPeriodType] = useState('quarterly');
  const [period, setPeriod] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const loadReports = async (number) => {
    if (!number) return;
    const response = await axios.get(`${API}/scholarship/utilization/${encodeURIComponent(number)}`, { headers });
    setReports(response.data.reports || []);
    setPaused(Boolean(response.data.application?.disbursement_paused));
    setMissing(response.data.application?.missing_utilization_period || '');
  };

  useEffect(() => {
    axios.get(`${API}/scholarship/mine`, { headers }).then((response) => {
      const approved = (response.data || []).filter((item) => item.status === 'approved');
      setApplications(approved);
      if (approved[0]) {
        setSelected(approved[0].application_number);
        loadReports(approved[0].application_number);
      }
    }).catch((err) => setError(errorText(err)));
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    const data = new FormData();
    data.append('application_number', selected);
    data.append('period_type', periodType);
    data.append('period', period);
    data.append('amount', amount);
    data.append('description', description);
    try {
      await axios.post(`${API}/scholarship/utilization`, data, { headers });
      setMessage('Utilization report submitted.');
      setDescription('');
      setAmount('');
      await loadReports(selected);
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-semibold text-gray-900">Scholarship utilization</h1>
        <p className="mt-2 text-sm text-gray-600">
          Each month or quarter, tell us how the scholarship was spent or invested in your studies.
          If a report is missing, the next disbursement stays paused until you submit it.
        </p>
        {applications.length === 0 ? (
          <p className="mt-6 text-sm text-gray-700">Utilization reporting opens after a scholarship is approved.</p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4 rounded-xl bg-white p-6 shadow-sm border border-gray-100">
            {paused && (
              <p className="text-sm text-amber-800">
                Disbursement is paused until the report for {missing} is submitted.
              </p>
            )}
            <div>
              <Label>Application</Label>
              <select
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                value={selected}
                onChange={(event) => {
                  setSelected(event.target.value);
                  loadReports(event.target.value);
                }}
              >
                {applications.map((item) => (
                  <option key={item.application_number} value={item.application_number}>
                    {item.application_number}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Period type</Label>
                <select className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm" value={periodType} onChange={(event) => setPeriodType(event.target.value)}>
                  <option value="quarterly">Quarterly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <Label>{periodType === 'monthly' ? 'Month (YYYY-MM)' : 'Quarter (YYYY-Q1)'}</Label>
                <Input className="mt-1" value={period} onChange={(event) => setPeriod(event.target.value)} placeholder={periodType === 'monthly' ? '2026-10' : '2026-Q3'} required />
              </div>
            </div>
            <div>
              <Label>Amount spent on academics (₹)</Label>
              <Input className="mt-1" type="number" min="0" value={amount} onChange={(event) => setAmount(event.target.value)} required />
            </div>
            <div>
              <Label>How were the funds used?</Label>
              <Textarea className="mt-1" value={description} onChange={(event) => setDescription(event.target.value)} required />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {message && <p className="text-sm text-green-700">{message}</p>}
            <Button type="submit" className="bg-[#A89968] hover:bg-[#8f8356] text-white">Submit utilization report</Button>
          </form>
        )}
        {reports.length > 0 && (
          <ul className="mt-6 space-y-2">
            {reports.map((report) => (
              <li key={report.id} className="rounded-lg bg-white p-3 text-sm border border-gray-100">
                <span className="font-medium">{report.period}</span> · ₹{report.amount} · {report.description}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
